import { Link, getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { api } from "@/lib/api";
import { NavBreadcrumbs, type BreadcrumbEntry } from "@/components/NavBreadcrumbs";
import { PaginationControls } from "@/components/ui/pagination-controls";
import "./results.$resultId.css";

const routeApi = getRouteApi("/results/$resultId");
const SEGMENTS_PAGE_SIZE = 10;

function shouldHideRank(
  rank: number | null | undefined,
  flags?: { dnf?: boolean | null; dns?: boolean | null; dq?: boolean | null },
): boolean {
  if (flags?.dnf || flags?.dns || flags?.dq) return true;
  if (rank == null) return true;
  return rank >= 99999;
}

export default function ResultPage() {
  const { resultId } = routeApi.useParams();
  const [segmentPage, setSegmentPage] = useState(1);

  useEffect(() => {
    setSegmentPage(1);
  }, [resultId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["result", resultId],
    queryFn: async () => {
      const { data, error } = await api.GET("/results/{id}", {
        params: { path: { id: resultId } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (error) return <p className="text-destructive">Error loading result</p>;
  if (!data) return null;

  const { athlete, event, segments, ...result } = data;
  const totalSegmentPages = Math.max(
    Math.ceil(segments.length / SEGMENTS_PAGE_SIZE),
    1,
  );
  const clampedSegmentPage = Math.min(segmentPage, totalSegmentPages);
  const startIndex = (clampedSegmentPage - 1) * SEGMENTS_PAGE_SIZE;
  const paginatedSegments = segments.slice(
    startIndex,
    startIndex + SEGMENTS_PAGE_SIZE,
  );
  const hideResultRanks = shouldHideRank(result.finishRankOverall, result);
  const highlightRank = hideResultRanks ? undefined : result.finishRankOverall ?? undefined;

  const breadcrumbItems: BreadcrumbEntry[] = [
    { label: "Records", to: "/records", search: {} },
  ];
  if (event.eventSeriesId) {
    breadcrumbItems.push({
      label: event.eventSeriesName ?? "Event Series",
      to: "/events/series/$seriesId",
      params: { seriesId: event.eventSeriesId },
    });
  }
  breadcrumbItems.push({
    label: event.name,
    to: "/events/$eventId",
    params: { eventId: event.id },
    search: {
      ageGroup: undefined,
      highlight: result.id,
      highlightRank,
      page: undefined,
    },
  });

  const flagIso = (athlete as any).representingCountryIso2 ?? (athlete as any).countryIso2;
  const location = [athlete.city, athlete.state, athlete.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="result-page">
      <NavBreadcrumbs items={breadcrumbItems} current={athlete.fullName} />

      {/* Hero */}
      <div className="result-hero">
        <p className="result-eyebrow">Race Result</p>
        <h1 className="result-event-name">
          <Link
            to="/events/$eventId"
            params={{ eventId: event.id }}
            search={{
              ageGroup: undefined,
              highlight: result.id,
              highlightRank,
              page: undefined,
            }}
          >
            {event.name}
          </Link>
        </h1>
        <div className="result-meta">
          {event.brand && <span>{event.brand}</span>}
          {event.raceType && <span className="result-pill">{event.raceType}</span>}
        </div>
      </div>

      {/* Athlete + Performance */}
      <div className="result-info-cards">
        <div className="result-info-card result-card-athlete">
          <p className="result-info-card-label">Athlete</p>
          <h2 className="result-athlete-name">
            <Link
              to="/athletes/$athleteId"
              params={{ athleteId: athlete.id }}
            >
              {flagIso && (
                <ReactCountryFlag
                  countryCode={flagIso}
                  svg
                  style={{
                    width: "0.9em",
                    height: "0.9em",
                    marginRight: "0.4em",
                    verticalAlign: "-0.05em",
                  }}
                />
              )}
              {athlete.fullName}
            </Link>
          </h2>
          {location && (
            <p className="result-athlete-location">{location}</p>
          )}
          <div className="result-athlete-pills">
            {athlete.gender && (
              <span className="result-pill">{athlete.gender}</span>
            )}
            {result.ageGroup && (
              <Link
                to="/events/$eventId"
                params={{ eventId: event.id }}
                search={{
                  ageGroup: result.ageGroup,
                  highlight: result.id,
                  highlightRank,
                  page: undefined,
                }}
                className="result-pill result-pill--link"
              >
                {result.ageGroup}
              </Link>
            )}
            {result.bib && (
              <span className="result-pill">Bib #{result.bib}</span>
            )}
          </div>
        </div>

        <div className="result-info-card result-card-performance">
          <p className="result-info-card-label">Finish</p>
          <p className="result-finish-time">
            {result.finishTimeFormatted ?? "—"}
          </p>
          <div className="result-rank-grid">
            <div className="result-rank-item">
              <span className="result-rank-label">Overall</span>
              <span className="result-rank-value">
                {hideResultRanks || shouldHideRank(result.finishRankOverall)
                  ? "—"
                  : `#${result.finishRankOverall}`}
              </span>
            </div>
            <div className="result-rank-item">
              <span className="result-rank-label">Gender</span>
              <span className="result-rank-value">
                {hideResultRanks || shouldHideRank(result.finishRankGender)
                  ? "—"
                  : `#${result.finishRankGender}`}
              </span>
            </div>
            <div className="result-rank-item">
              <span className="result-rank-label">Age Group</span>
              <span className="result-rank-value">
                {hideResultRanks || shouldHideRank(result.finishRankGroup)
                  ? "—"
                  : `#${result.finishRankGroup}`}
              </span>
            </div>
          </div>
          {(result.totalDistance || result.points) && (
            <div className="result-finish-extras">
              {result.totalDistance && <span>{result.totalDistance} km</span>}
              {result.points && <span>{result.points} pts</span>}
            </div>
          )}
        </div>
      </div>

      {/* Status flags */}
      {(result.dns || result.dnf || result.dq) && (
        <div className="result-status-flags">
          {result.dns && (
            <span className="result-status-pill result-status-pill--dns">DNS</span>
          )}
          {result.dnf && (
            <span className="result-status-pill result-status-pill--dnf">DNF</span>
          )}
          {result.dq && (
            <span className="result-status-pill result-status-pill--dq">DQ</span>
          )}
        </div>
      )}

      {/* Segments */}
      {segments.length > 0 && (
        <div className="result-segments-card">
          <p className="result-segments-title">Segments</p>
          <div className="result-segments-table-wrap">
            <table className="result-segments-table">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th className="text-right">Time</th>
                  <th className="text-right">Distance</th>
                  <th className="text-right"># Overall</th>
                  <th className="text-right"># Gender</th>
                  <th className="text-right"># Group</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSegments.map((seg) => (
                  <tr key={seg.id}>
                    <td className="result-segment-type">{seg.segmentType}</td>
                    <td className="text-right">
                      {seg.durationFormatted ?? "—"}
                    </td>
                    <td className="text-right">
                      {seg.distance ? `${seg.distance} km` : "—"}
                    </td>
                    <td className="text-right">
                      {shouldHideRank(seg.rankOverall) ? "—" : (seg.rankOverall ?? "—")}
                    </td>
                    <td className="text-right">
                      {shouldHideRank(seg.rankGender) ? "—" : (seg.rankGender ?? "—")}
                    </td>
                    <td className="text-right">
                      {shouldHideRank(seg.rankGroup) ? "—" : (seg.rankGroup ?? "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={clampedSegmentPage}
            pageSize={SEGMENTS_PAGE_SIZE}
            totalCount={segments.length}
            currentCount={paginatedSegments.length}
            onPageChange={(nextPage) =>
              setSegmentPage(
                Math.max(1, Math.min(nextPage, totalSegmentPages)),
              )
            }
            itemLabel="segments"
            className="result-segments-pagination"
          />
        </div>
      )}
    </div>
  );
}
